import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterConfig } from "@/components/ChipFilterGroup/ChipFilterGroup";
import {
  BaseEntity,
  FetchEntitiesFn,
} from "@/components/EntityPickerModal/EntityPickerModal";
import { fetchTradesFilterOptions } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchTradeFilterOptions/fetchTradeFilterOptionsApi";
import { fetchYearFilterOptions } from "@/modules/stochastic/features/Reports/SCDashboard/api/fetchYearFilterOptions/fetchYearFilterOptionsApi";
import { MPDashboardFilters } from "../utils/mpDashboardFilterParams";

const YEARS_KEY = "years";
const TRADES_KEY = "trades";
const TRADE_LOOKUP_PAGE_SIZE = 100;

export interface MPDashboardChipFilters {
  filterConfigs: FilterConfig<BaseEntity>[];
  filterValues: Record<string, BaseEntity[]>;
  handleFilterChange: (
    key: string,
    value: BaseEntity[] | BaseEntity | null,
  ) => void;
  handleFilterClearAll: () => void;
}

interface UseMPDashboardChipFiltersParams {
  years: string[];
  trades: string[];
  updateFilters: (changes: Partial<MPDashboardFilters>) => void;
}

const fetchYearEntities: FetchEntitiesFn<BaseEntity> = async ({
  searchTerm,
  page,
  pageSize,
}) => {
  const response = await fetchYearFilterOptions({
    searchTerm,
    page,
    pageSize,
    sortBy: "year",
    sortOrder: "DESC",
  });

  return {
    data: response.data.map((option) => ({
      id: String(option.name),
      name: String(option.name),
    })),
    totalCount: response.meta?.totalCount ?? response.data.length,
  };
};

const fetchTradeEntities: FetchEntitiesFn<BaseEntity> = async ({
  searchTerm,
  page,
  pageSize,
}) => {
  const response = await fetchTradesFilterOptions({
    searchTerm,
    page,
    pageSize,
    sortBy: "name",
    sortOrder: "ASC",
  });

  return {
    data: response.data.map((option) => ({
      id: String(option.id),
      name: option.name,
    })),
    totalCount: response.meta?.totalCount ?? response.data.length,
  };
};

const toIds = (value: BaseEntity[] | BaseEntity | null): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.map((entity) => String(entity.id));
};

export function useMPDashboardChipFilters({
  years,
  trades,
  updateFilters,
}: UseMPDashboardChipFiltersParams): MPDashboardChipFilters {
  const [tradeNamesById, setTradeNamesById] = useState<
    Record<string, string>
  >({});

  const hasUnnamedTrades = trades.some((id) => !(id in tradeNamesById));

  useEffect(() => {
    if (!hasUnnamedTrades) return;

    fetchTradeEntities({
      searchTerm: "",
      page: 1,
      pageSize: TRADE_LOOKUP_PAGE_SIZE,
    }).then(({ data }) => {
      setTradeNamesById((current) => ({
        ...current,
        ...Object.fromEntries(data.map((trade) => [trade.id, trade.name])),
      }));
    });
  }, [hasUnnamedTrades]);

  const filterConfigs: FilterConfig<BaseEntity>[] = useMemo(
    () => [
      {
        key: YEARS_KEY,
        label: "Years",
        type: "multi" as const,
        color: "green" as const,
        entityNameSingular: "Year",
        entityNamePlural: "Years",
        fetchEntities: fetchYearEntities,
      },
      {
        key: TRADES_KEY,
        label: "Trades",
        type: "multi" as const,
        color: "blue" as const,
        entityNameSingular: "Trade",
        entityNamePlural: "Trades",
        fetchEntities: fetchTradeEntities,
      },
    ],
    [],
  );

  const filterValues = useMemo(
    () => ({
      [YEARS_KEY]: years.map((year) => ({ id: year, name: year })),
      [TRADES_KEY]: trades.map((id) => ({
        id,
        name: tradeNamesById[id] ?? id,
      })),
    }),
    [years, trades, tradeNamesById],
  );

  const handleFilterChange = useCallback(
    (key: string, value: BaseEntity[] | BaseEntity | null) => {
      if (key === YEARS_KEY) {
        updateFilters({ years: toIds(value) });
        return;
      }

      if (Array.isArray(value)) {
        setTradeNamesById((current) => ({
          ...current,
          ...Object.fromEntries(
            value.map((trade) => [String(trade.id), trade.name]),
          ),
        }));
      }
      updateFilters({ trades: toIds(value) });
    },
    [updateFilters],
  );

  const handleFilterClearAll = useCallback(() => {
    updateFilters({ years: [], trades: [] });
  }, [updateFilters]);

  return {
    filterConfigs,
    filterValues,
    handleFilterChange,
    handleFilterClearAll,
  };
}
